<template>
  <!-- Listado de correspondencia: registra comunicaciones con código autogenerado
       (SAP-Emisor-Receptor-Fecha-Número) y detrás, solo en visual, ": asunto".
       Interna: emisor y receptor son disciplinas. Externa: emisor fijo TKAS, receptor un cliente. -->
  <div class="corr-list" :class="{ mark }">
    <!-- Panel: alta de una correspondencia (campos en rejilla + fila de acciones). -->
    <div class="cl-panel">
      <div class="cl-grid">
        <div class="cl-field">
          <label>{{ $t('prm_summary_s04_project_sap') }}</label>
          <input class="cl-ctrl ro" type="text" :value="sap" readonly>
        </div>
        <div class="cl-field">
          <label>{{ $t('prm_summary_s04_corr_type') }}</label>
          <select v-model="type" class="cl-ctrl" @change="onTypeChange">
            <option value="internal">{{ $t('prm_summary_s04_corr_internal') }}</option>
            <option value="external">{{ $t('prm_summary_s04_corr_external') }}</option>
          </select>
        </div>
        <div class="cl-field">
          <label>{{ $t('prm_summary_s04_col_issuer') }}</label>
          <select v-if="type === 'internal'" v-model="issuerCode" class="cl-ctrl">
            <option value="">{{ $t('prm_summary_s04_select_discipline') }}</option>
            <option v-for="o in disciplineOpts" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <input v-else class="cl-ctrl ro" type="text" value="TKAS" readonly>
        </div>
        <div class="cl-field">
          <label>{{ $t('prm_summary_s04_col_receiver') }}</label>
          <select v-model="receiverCode" class="cl-ctrl">
            <option value="">{{ type === 'external' ? $t('prm_summary_s04_select_client') : $t('prm_summary_s04_select_discipline') }}</option>
            <option v-for="o in receiverOpts" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
        </div>
        <div class="cl-field">
          <label>{{ $t('prm_summary_s04_col_date') }}</label>
          <input v-model="date" class="cl-ctrl" type="date">
        </div>
        <div class="cl-field">
          <label>{{ $t('prm_summary_s04_col_responsible') }}</label>
          <select v-model="responsible" class="cl-ctrl">
            <option value="">{{ $t('prm_summary_s04_select_user') }}</option>
            <option v-for="o in ownerOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <span v-if="!ownerOptions.length" class="cl-hint">{{ $t('prm_summary_s04_no_users') }}</span>
        </div>
        <div class="cl-field">
          <label>{{ $t('prm_summary_s04_col_subject') }}</label>
          <input v-model="subject" class="cl-ctrl" type="text" :placeholder="$t('prm_summary_s04_subject_ph')">
        </div>
      </div>
      <div class="cl-actions">
        <button type="button" class="cl-btn ghost" @click="pickStaged">
          <b-icon pack="fas" icon="paperclip" size="is-small"></b-icon><span>{{ $t('prm_summary_s04_attach_file') }}</span>
        </button>
        <input ref="stagedInput" type="file" accept="application/pdf" class="cl-hidden" @change="onStaged">
        <span v-if="stagedFile" class="cl-staged">
          <b-icon pack="fas" icon="file-pdf" size="is-small"></b-icon>
          <span class="cl-staged-name" v-tip="stagedFile.name">{{ stagedFile.name }}</span>
          <button type="button" class="cl-staged-x" @click="clearStaged"><b-icon pack="fas" icon="xmark" size="is-small"></b-icon></button>
        </span>
        <button class="cl-btn primary" :disabled="!canRegister || registering" @click="addCorr">
          <b-icon pack="fas" :icon="registering ? 'spinner' : 'plus'" :custom-class="registering ? 'fa-spin' : ''" size="is-small"></b-icon><span>{{ $t('prm_summary_s04_register_corr') }}</span>
        </button>
      </div>
    </div>

    <!-- Panel de filtros. -->
    <div class="cl-filter-panel">
      <div class="cl-field grow">
        <label>{{ $t('prm_summary_s04_search') }}</label>
        <div class="cl-search">
          <b-icon pack="fas" icon="magnifying-glass" size="is-small" class="cl-search-ic"></b-icon>
          <input v-model="search" class="cl-ctrl srch" type="text" :placeholder="$t('prm_summary_s04_search_corr')">
        </div>
      </div>
      <div class="cl-field">
        <label>{{ $t('prm_summary_s04_filter_type') }}</label>
        <select v-model="filterType" class="cl-ctrl">
          <option value="">{{ $t('prm_summary_s04_all') }}</option>
          <option value="internal">{{ $t('prm_summary_s04_corr_internal') }}</option>
          <option value="external">{{ $t('prm_summary_s04_corr_external') }}</option>
        </select>
      </div>
      <p class="cl-count">{{ filteredRows.length }} {{ $t('prm_summary_of') }} {{ localRows.length }} {{ $t('prm_summary_records') }}</p>
    </div>

    <!-- Card de la tabla de registros. -->
    <div class="cl-table-card">
      <table>
        <thead>
          <tr>
            <th>{{ $t('prm_summary_s04_project_sap') }}</th>
            <th>{{ $t('prm_summary_s04_col_issuer') }}</th>
            <th>{{ $t('prm_summary_s04_col_receiver') }}</th>
            <th>{{ $t('prm_summary_s04_col_date') }}</th>
            <th>{{ $t('prm_summary_s04_col_num') }}</th>
            <th>{{ $t('prm_summary_s04_col_responsible') }}</th>
            <th>{{ $t('prm_summary_s04_col_subject') }}</th>
            <th>{{ $t('prm_summary_s04_col_full_code') }}</th>
            <th>{{ $t('prm_summary_s04_col_file_versions') }}</th>
            <th>{{ $t('prm_summary_s03_actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in filteredRows">
          <tr :key="row.id">
            <td>{{ sap }}</td>
            <td>{{ row.issuerCode }}</td>
            <td>{{ row.receiverCode }}</td>
            <td>{{ compactDate(row.date) }}</td>
            <td>{{ row.number }}</td>
            <td>{{ row.responsible }}</td>
            <td class="subj">{{ row.subject }}</td>
            <td class="code">{{ displayCode(row) }}</td>
            <td>
              <prm-versioned-file :file="fileFor(row)" :editing="true" :project-id="projectId"
                :open="historyRow === row.id" :upload-url="uploadUrl(row)"
                @uploaded="onFileUploaded" @toggle-history="toggleHistory(row.id)"></prm-versioned-file>
            </td>
            <td class="acts">
              <button class="cl-copy" @click="copyCode(row)">{{ $t('prm_summary_s04_copy') }}</button>
              <button class="cl-del" @click="removeRow(row)"><b-icon pack="fas" icon="trash-can" size="is-small"></b-icon><span>{{ $t('prm_summary_s04_delete') }}</span></button>
            </td>
          </tr>
          <tr v-if="historyRow === row.id && fileFor(row)" :key="row.id + '-h'" class="hist-tr">
            <td :colspan="10"><prm-file-history :file="fileFor(row)" :base-url="baseUrl" :project-id="projectId" @close="historyRow = null"></prm-file-history></td>
          </tr>
          </template>
          <tr v-if="!filteredRows.length"><td :colspan="10" class="empty">{{ $t('prm_summary_s04_no_corr') }}</td></tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import PrmVersionedFile from './versioned-file.vue'
  import PrmFileHistory from './file-history.vue'
  import FileUploader from '~/plugins/fileUploader'
  import { clientCodeOptions } from '../shared/airports'
  import { disciplineOptions } from '../shared/doc-catalogs'
  import { tip } from '../shared/tip'

  let seq = 0
  function genId () { return 'c' + Date.now().toString(36) + (seq++).toString(36) + Math.random().toString(36).slice(2, 6) }

  export default {
    name: 'PrmCorrespondenceList',

    components: { PrmVersionedFile, PrmFileHistory },

    directives: { tip },

    props: {
      rows: { type: Array, default: () => [] },          // s04.correspondence
      sap: { type: String, default: '' },
      ownerOptions: { type: Array, default: () => [] },  // participantes del proyecto
      projectId: { type: [Number, String], default: null },
      baseUrl: { type: String, default: '' },
      files: { type: Object, default: () => ({}) },      // sources.contractFiles.correspondence
      mark: { type: Boolean, default: false },
      resetKey: { type: Number, default: 0 },
    },

    data: () => ({
      localRows: [],
      type: 'internal',
      issuerCode: '',
      receiverCode: '',
      date: '',
      responsible: '',
      subject: '',
      search: '',
      filterType: '',
      historyRow: null,
      stagedFile: null, // PDF adjuntado en el alta, se sube al registrar
      registering: false,
    }),

    computed: {
      disciplineOpts () { return disciplineOptions() },
      clientOptions () { return clientCodeOptions() },
      // Receptor: cliente (externa) o disciplina (interna).
      receiverOpts () { return this.type === 'external' ? this.clientOptions : this.disciplineOpts },
      // Emisor efectivo: TKAS fijo en externa; la disciplina elegida en interna.
      effectiveIssuer () { return this.type === 'external' ? 'TKAS' : this.issuerCode },
      canRegister () {
        return !!this.effectiveIssuer && !!this.receiverCode && !!this.date && !!this.responsible && !!this.subject.trim()
      },
      filteredRows () {
        const q = this.search.trim().toLowerCase()
        return this.localRows.filter((r) => {
          if (this.filterType && r.type !== this.filterType) return false
          if (q) {
            const hay = (this.displayCode(r) + ' ' + (r.issuerCode || '') + ' ' + (r.receiverCode || '')).toLowerCase()
            if (!hay.includes(q)) return false
          }
          return true
        })
      },
    },

    watch: {
      resetKey: { immediate: true, handler () { this.init() } },
    },

    created () {
      // Fecha por defecto: hoy (como el diseño). Se persiste en formato Y-m-d.
      this.date = new Date().toISOString().slice(0, 10)
    },

    methods: {
      init () {
        this.localRows = (this.rows || []).map((r) => ({ ...r, id: r.id || genId() }))
      },

      // Fecha compacta YYYYMMDD (del Y-m-d guardado) para el código y la columna.
      compactDate (ymd) { return String(ymd || '').replace(/-/g, '') },

      // Código = SAP-Emisor-Receptor-YYYYMMDD-Número (sin el asunto).
      code (row) {
        return [this.sap, row.issuerCode, row.receiverCode, this.compactDate(row.date), row.number].join('-')
      },
      // Lo que se muestra: el código y, solo visual, ": asunto".
      displayCode (row) {
        const base = this.code(row)
        return row.subject ? `${base}: ${row.subject}` : base
      },

      fileFor (row) { return this.files[row.id] || null },
      uploadUrl (row) { return `${this.baseUrl}/contract-files/correspondence/${row.id}` },
      onFileUploaded () { this.$emit('files-changed') },
      toggleHistory (rowId) { this.historyRow = this.historyRow === rowId ? null : rowId },

      onTypeChange () {
        // Cambian los catálogos de emisor/receptor: limpiamos las selecciones.
        this.issuerCode = ''
        this.receiverCode = ''
      },

      // Número correlativo por combinación (emisor+receptor+fecha): si ya existe, +1; si no, 0001.
      nextNumber (issuerCode, receiverCode, dateCompact) {
        let max = 0
        for (const r of this.localRows) {
          if (r.issuerCode === issuerCode && r.receiverCode === receiverCode && this.compactDate(r.date) === dateCompact) {
            const n = parseInt(r.number, 10) || 0
            if (n > max) max = n
          }
        }
        return String(max + 1).padStart(4, '0')
      },

      async addCorr () {
        if (!this.canRegister || this.registering) return
        this.registering = true
        const id = genId()
        const issuerCode = this.effectiveIssuer
        const number = this.nextNumber(issuerCode, this.receiverCode, this.compactDate(this.date))
        this.localRows.push({
          id,
          type: this.type,
          issuerCode,
          receiverCode: this.receiverCode,
          date: this.date,
          number,
          responsible: this.responsible,
          subject: this.subject.trim(),
        })
        this.emitRows()
        // Si adjuntaron un PDF en el alta, lo subimos a la fila recién creada.
        await this.uploadStaged(id)
        // Dejamos el tipo y responsable; limpiamos el resto para el siguiente registro.
        this.issuerCode = ''
        this.receiverCode = ''
        this.subject = ''
        this.registering = false
      },

      // Adjunto previo al registro: guardamos el PDF y lo subimos al crear la fila.
      pickStaged () { this.$refs.stagedInput.click() },
      onStaged (e) {
        const f = e.target.files && e.target.files[0]
        e.target.value = ''
        if (!f) return
        if (f.type !== 'application/pdf' && !/\.pdf$/i.test(f.name)) { this.$notify.error(this.$t('prm_summary_s02_pdf_only')); return }
        this.stagedFile = f
      },
      clearStaged () { this.stagedFile = null },
      async uploadStaged (rowId) {
        if (!this.stagedFile) return
        try {
          const ok = await FileUploader.submitFile(this.stagedFile, {
            entityType: 'prm_contract_file', entityId: 0, projectId: this.projectId,
            uploadEndpoint: `${this.baseUrl}/contract-files/correspondence/${rowId}`,
          })
          if (ok) this.$emit('files-changed')
          else this.$notify.error(this.$t('error_saving_data'))
        } catch (e) { this.$notify.error(this.$t('error_saving_data')) }
        this.stagedFile = null
      },

      removeRow (row) {
        const i = this.localRows.findIndex((r) => r.id === row.id)
        if (i < 0) return
        const file = this.fileFor(row)
        this.localRows.splice(i, 1)
        this.emitRows()
        if (file) this.$emit('remove-file', file.id)
      },

      async copyCode (row) {
        try {
          await navigator.clipboard.writeText(this.code(row))
          this.$notify.success(this.$t('prm_summary_s04_copied'))
        } catch (e) {
          this.$notify.error(this.$t('error_saving_data'))
        }
      },

      emitRows () {
        this.$emit('save-list', { path: 's04.correspondence', items: this.localRows.map((r) => ({ ...r })) })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .corr-list { padding: 12px; display: flex; flex-direction: column; gap: 12px; }

  // Panel de formulario (blanco): rejilla de campos + fila de acciones.
  .cl-panel {
    display: flex; flex-direction: column; gap: 10px;
    border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px;
  }
  .cl-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; align-items: end; }
  @media (max-width: 900px) { .cl-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 560px) { .cl-grid { grid-template-columns: 1fr; } }
  .cl-grid .cl-field { min-width: 0; }
  .cl-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }

  // Panel de filtros (gris), campos alineados abajo.
  .cl-filter-panel {
    display: flex; flex-wrap: wrap; align-items: flex-end; gap: 8px;
    border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px; background: #f8fafc;
  }

  .cl-field { display: flex; flex-direction: column; gap: 4px; min-width: 140px; }
  .cl-field.grow { flex: 1 1 180px; }
  .cl-field label { font-size: 10px; font-weight: 600; color: #475569; }
  .cl-hint { font-size: 9px; color: #b5762a; }

  .cl-ctrl {
    width: 100%; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 4px;
    padding: 6px 8px; font-size: 11px; color: #334155; background: #fff; outline: none;
  }
  .cl-ctrl:focus { border-color: #3b82f6; }
  .cl-ctrl::placeholder { color: #94a3b8; }
  .cl-ctrl.ro { background: #f4f6f9; color: #6b7787; }

  .cl-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 4px;
    border-radius: 4px; padding: 6px 12px; font-size: 10px; font-weight: 600; cursor: pointer;
    white-space: nowrap; border: 1px solid transparent;
  }
  .cl-btn.primary { background: #2f5f8f; color: #fff; }
  .cl-btn.primary:hover:not(:disabled) { background: #27527c; }
  .cl-btn.ghost { background: #fff; border-color: #9fb5cc; color: #2f5f8f; padding: 6px 10px; }
  .cl-btn.ghost:hover:not(:disabled) { background: #f6f9fc; }
  .cl-btn:disabled { opacity: .5; cursor: not-allowed; }

  // Adjunto previo (Adjuntar archivo + nombre del PDF elegido).
  .cl-hidden { display: none; }
  .cl-staged { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; color: #475569; }
  .cl-staged-name { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cl-staged-x { border: 0; background: transparent; color: #9ca3af; cursor: pointer; padding: 0; line-height: 1; }
  .cl-staged-x:hover { color: #dc2626; }

  .cl-search { position: relative; }
  .cl-search-ic { position: absolute; left: 8px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; z-index: 1; }
  .cl-ctrl.srch { padding-left: 26px; }
  .cl-count { font-size: 10px; color: #64748b; margin: 0 0 0 auto; align-self: center; }

  // Card de la tabla.
  .cl-table-card { border: 1px solid #b8c7d8; border-radius: 4px; overflow-x: auto; }
  table { width: 100%; min-width: 900px; border-collapse: collapse; font-size: 10px; }
  thead { background: #3d6b9a; }
  th { color: #fff; font-size: 10px; font-weight: 700; text-align: left; padding: 6px 8px; }
  td { padding: 8px 8px; font-size: 10px; color: #334155; vertical-align: middle; border-top: 1px solid #e2e8f0; }
  td.subj { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  td.code { color: #33475f; }
  td.empty { text-align: center; color: #9aa4b2; padding: 14px; border-top: 0; }
  .hist-tr td { padding: 6px 10px; background: #fafbfc; }
  .corr-list.mark tbody td { background: #fff8e6; }

  .cl-copy { border: 0; background: transparent; color: #2f5f8f; cursor: pointer; font-size: 10px; margin-right: 8px; padding: 0; }
  .cl-copy:hover { text-decoration: underline; }
  .cl-del { display: inline-flex; align-items: center; gap: 4px; border: 0; background: transparent; color: #b91c1c; cursor: pointer; font-size: 10px; padding: 0; }
  .cl-del:hover { text-decoration: underline; }
  td.acts { white-space: nowrap; }
</style>
